import type {
  TimelineData,
  TimelineEntity,
  TimelineExitKind,
} from '@revelio/replots';

import type { CompanySearchApiResult } from '../../../../../shared/api/fetchCompanySearchResults';

/** One dated parent link, as the `subsidiaries-lineage` saved query returns it. */
export type LineageRow = {
  ID: number | string;
  PARENT_ID: number | string | null;
  START_DATE: string | null;
  END_DATE: string | null;
  /** When a company still filed under the group was dissolved. */
  DISSOLVED_ON?: string | null;
  EXIT_KIND?: string | null;
  EXIT_TO?: string | null;
  /** Company-universe name, for companies the mapper does not list under the group. */
  NAME?: string | null;
};

export type BuildSubsidiaryLineageInput = {
  rows: LineageRow[];
  /** The group's own rcid and name, from the primary company chip. */
  root: { rcid: number; name: string };
  /** Mapper rows, which carry the names and employee counts. */
  companies: CompanySearchApiResult[];
  /** Screener founding years, when known: where a company's own track starts. */
  foundedYearByRcid?: Map<number, number>;
};

const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;
/** Joins older than this before the last one are stamps, not history worth an axis. */
const MAX_LEAD_IN_YEARS = 12;
/** Founding years older than this before the last join are cut off by the axis. */
const MAX_FOUNDING_LEAD_IN_YEARS = 45;
const EARLIEST_PLAUSIBLE_FOUNDING = 1700;
const EARLIEST_AXIS_START = Date.UTC(1990, 0, 1);

const EXIT_KINDS: ReadonlySet<string> = new Set<TimelineExitKind>([
  'sold',
  'independent',
  'jv',
  'listed',
  'dissolved',
  'dissolved_undated',
]);

const exitKindOf = (
  value: string | null | undefined
): TimelineExitKind | undefined =>
  value && EXIT_KINDS.has(value) ? (value as TimelineExitKind) : undefined;

const toNumber = (value: number | string | null): number | null => {
  if (value === null) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isoDate = (value: string | null): string | undefined => {
  if (value === null) return undefined;
  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}/.test(trimmed) ? trimmed.slice(0, 10) : undefined;
};

/**
 * Chart data for the subsidiaries timeline: FactSet supplies who joined whom
 * and when, company-mapper the names and sizes, the screener the founding years.
 *
 * The mapper's employee count is rolled up — a parent's figure already holds
 * its subsidiaries — so each company is sized by what is left once its direct
 * children are taken out, and the chart adds the children back as they join.
 *
 * A company the mapper does not know — one that has left the group, or that
 * the mapper's release predates — still gets a band, sized at zero and
 * labelled by its company-universe name (its rcid as a last resort), so it
 * stays visible rather than silently dropped.
 * Rows whose parent is missing from the payload hang off the group root, since
 * a band with no parent would never merge into anything.
 */
export const buildSubsidiaryLineage = ({
  rows,
  root,
  companies,
  foundedYearByRcid,
}: BuildSubsidiaryLineageInput): TimelineData => {
  const byRcid = new Map(
    companies.map((company) => [company.rcid, company] as const)
  );
  const known = new Set<number>([root.rcid]);
  for (const row of rows) {
    const id = toNumber(row.ID);
    if (id !== null) known.add(id);
  }
  const rawCount = (rcid: number): number =>
    Math.max(byRcid.get(rcid)?.raw_emp_count ?? 0, 0);
  const childrenRaw = new Map<number, number>();
  for (const row of rows) {
    const id = toNumber(row.ID);
    const parentId = toNumber(row.PARENT_ID);
    if (id === null || parentId === null || !known.has(parentId)) continue;
    if (isoDate(row.END_DATE) !== undefined) continue;
    childrenRaw.set(parentId, (childrenRaw.get(parentId) ?? 0) + rawCount(id));
  }
  const ownCount = (rcid: number): number =>
    Math.max(rawCount(rcid) - (childrenRaw.get(rcid) ?? 0), 0);
  const thisYear = new Date().getUTCFullYear();
  const foundedYear = (rcid: number): number | undefined => {
    const year = foundedYearByRcid?.get(rcid);
    return year !== undefined &&
      year >= EARLIEST_PLAUSIBLE_FOUNDING &&
      year <= thisYear
      ? year
      : undefined;
  };

  const entities: TimelineEntity[] = [
    { id: String(root.rcid), label: root.name },
  ];

  for (const row of rows) {
    const id = toNumber(row.ID);
    if (id === null || id === root.rcid) continue;
    const parentId = toNumber(row.PARENT_ID);
    const company = byRcid.get(id);
    const joinedAt = isoDate(row.START_DATE);
    const endedAt = isoDate(row.END_DATE) ?? isoDate(row.DISSOLVED_ON ?? null);
    const exitKind = exitKindOf(row.EXIT_KIND);
    const exitTo = row.EXIT_TO?.trim();
    const own = ownCount(id);
    const founded = foundedYear(id);

    entities.push({
      id: String(id),
      label:
        company?.primary_name?.trim() || row.NAME?.trim() || `Company ${id}`,
      parentId: String(
        parentId !== null && known.has(parentId) ? parentId : root.rcid
      ),
      ...(own > 0 ? { value: own } : {}),
      ...(founded !== undefined ? { startedAt: String(founded) } : {}),
      ...(joinedAt !== undefined ? { joinedAt } : {}),
      ...(endedAt !== undefined ? { endedAt } : {}),
      ...(exitKind !== undefined ? { exitKind } : {}),
      ...(exitKind === 'sold' && exitTo ? { exitTo } : {}),
    });
  }

  // FactSet stamps companies it has always known with dates from the 1900s.
  // Anchoring the axis there would squeeze every real acquisition into the last
  // centimetre, so the window starts a few years before the first real join.
  const joinTimes = entities
    .map((entity) => entity.joinedAt)
    .filter((value): value is string => value !== undefined)
    .map((value) => Date.parse(`${value}T00:00:00Z`))
    .filter((value) => !Number.isNaN(value))
    .sort((a, b) => a - b);
  const latest = joinTimes[joinTimes.length - 1];
  const floor =
    latest === undefined ? undefined : latest - MAX_LEAD_IN_YEARS * YEAR_MS;
  const earliestShown = joinTimes.find(
    (time) => floor === undefined || time >= floor
  );
  // Founding years pull the axis back so a company's run before its deal is
  // visible, within reason: a 19th-century founding would flatten it again.
  const foundingFloor =
    latest === undefined
      ? undefined
      : latest - MAX_FOUNDING_LEAD_IN_YEARS * YEAR_MS;
  const earliestFounding = entities
    .map((entity) => entity.startedAt)
    .filter((value): value is string => value !== undefined)
    .map((value) => Date.UTC(Number(value), 0, 1))
    .filter((time) => foundingFloor === undefined || time >= foundingFloor)
    .sort((a, b) => a - b)[0];
  const axisStart =
    earliestShown === undefined
      ? undefined
      : Math.max(
          EARLIEST_AXIS_START,
          Math.min(
            earliestShown - YEAR_MS,
            earliestFounding ?? Number.POSITIVE_INFINITY
          )
        );
  const startDate =
    axisStart === undefined
      ? undefined
      : new Date(axisStart).toISOString().slice(0, 10);

  return {
    entities,
    ...(startDate !== undefined ? { startDate } : {}),
  };
};
